import type Phaser from "phaser";

/**
 * The shake of a prop's image (ADR-142's amendment of 2026-10-31): a few pixels across and back, for a game that
 * shakes a prop it acts on, a harvested tuft. A shake plays through the scene's tweens, one at a time for a prop; a scene
 * with no tweens, or a zero length, shakes nothing. The image is always put back exactly at the rest the caller gave.
 */
export class PropShake {
  private readonly running = new Map<string, { readonly tween: Phaser.Tweens.Tween; readonly image: Phaser.GameObjects.Image; readonly rest: number }>();

  /**
   * @param scene The scene whose tweens drive a shake.
   */
  public constructor(private readonly scene: Phaser.Scene) {}

  /**
   * Whether a prop shakes now.
   *
   * @param id The prop's id.
   * @returns True while it does.
   */
  public shaking(id: string): boolean {
    return this.running.has(id);
  }

  /**
   * Shakes a prop's image: it swings across by up to `pixels` and back twice over `durationMs`, and ends at `rest`. A
   * shake already running on the prop is stopped and its image put back first.
   *
   * @param id The prop's id.
   * @param image The prop's image.
   * @param rest The image's resting place across, in world pixels.
   * @param pixels The widest swing, in whole pixels.
   * @param durationMs How long the shake lasts.
   * @returns Whether a shake started.
   */
  public start(id: string, image: Phaser.GameObjects.Image, rest: number, pixels: number, durationMs: number): boolean {
    this.stop(id);
    const tweens = this.scene.tweens as Phaser.Tweens.TweenManager | undefined;
    if (tweens === undefined || durationMs <= 0 || pixels <= 0) {
      return false;
    }
    const tween = tweens.addCounter({
      from: 0,
      to: 1,
      duration: durationMs,
      onUpdate: (counter: Phaser.Tweens.Tween) => {
        image.setX(rest + Math.round(pixels * Math.sin((counter.getValue() ?? 0) * Math.PI * 4)));
      },
      onComplete: () => {
        this.running.delete(id);
        image.setX(rest);
      },
    });
    this.running.set(id, { tween, image, rest });
    return true;
  }

  /**
   * Stops a prop's shake, if one runs, and puts its image back at rest.
   *
   * @param id The prop's id.
   */
  public stop(id: string): void {
    const shake = this.running.get(id);
    if (shake !== undefined) {
      shake.tween.stop();
      shake.image.setX(shake.rest);
      this.running.delete(id);
    }
  }
}
